import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
// import { useSelector } from "react-redux";
// import { selectUser } from "../redux/authSlice";
import { useAuth } from "../context/AuthContext";

import { apiJson } from "../api/apiClient";
import "../styles/PaymentPage.css";

const fmt = (n, sym = "₹") => {
  if (n == null || n === "") return "—";
  return sym + parseFloat(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function loadStripe(publishableKey) {
  return new Promise((resolve) => {
    if (window.Stripe) return resolve(window.Stripe(publishableKey));
    const s = document.createElement("script");
    s.src = "https://js.stripe.com/v3/";
    s.onload = () => resolve(window.Stripe(publishableKey));
    s.onerror = () => resolve(null);
    document.body.appendChild(s);
  });
}

// Elegant Row Component
const ElegantRow = ({ label, value, accent, bold, borderTop, sub }) => {
  const rowClass = `payment-row ${borderTop ? 'payment-row-border-top' : ''}`;
  const labelClass = `payment-row-label ${sub ? 'payment-row-label-sub' : ''}`;
  const valueClass = `payment-row-value ${sub ? 'payment-row-value-sub' : ''}`;

  return (
    <div className={rowClass}>
      <span className={labelClass} style={{ color: accent || (bold ? 'var(--primary-color)' : 'var(--text-muted)'), fontWeight: bold ? '700' : '400' }}>
        {label}
      </span>
      <span className={valueClass} style={{ color: accent || (bold ? 'var(--primary-color)' : 'var(--text-dark)'), fontWeight: bold ? '800' : '400' }}>
        {value}
      </span>
    </div>
  );
};

// GrandTotalBreakdown Component
const GrandTotalBreakdown = ({ breakdown, sym = "₹" }) => {
  const [expanded, setExpanded] = useState(false);
  if (!breakdown) return null;

  const f = (n) => fmt(n, sym);
  const labels = breakdown.fee_labels || {};

  const extraFees = [
    breakdown.processing_fee > 0 && { label: labels.processing || "Processing Fee", val: breakdown.processing_fee },
    breakdown.stamp_duty > 0 && { label: labels.stamp_duty || "Stamp Duty", val: breakdown.stamp_duty },
    breakdown.registration_fee > 0 && { label: labels.registration || "Registration Fee", val: breakdown.registration_fee },
    breakdown.maintenance_charge > 0 && { label: labels.maintenance || "Maintenance", val: breakdown.maintenance_charge },
    breakdown.custom_fee1 > 0 && { label: labels.custom1 || "Custom Fee 1", val: breakdown.custom_fee1 },
    breakdown.custom_fee2 > 0 && { label: labels.custom2 || "Custom Fee 2", val: breakdown.custom_fee2 },
  ].filter(Boolean);

  const hasCharges = extraFees.length > 0 || breakdown.tax_amount > 0;
  if (!hasCharges) return null;

  return (
    <div className="payment-breakdown-card">
      <button type="button" className="payment-breakdown-header" onClick={() => setExpanded(e => !e)}>
        <span className="payment-breakdown-title">📄 Grand Total Breakdown</span>
        <span className="payment-breakdown-toggle">{expanded ? "▲ Hide" : "▼ Show"}</span>
      </button>

      {expanded && (
        <div className="payment-breakdown-content">
          <ElegantRow label="Unit Price" value={f(breakdown.unit_price)} />
          {extraFees.map((fee, i) => <ElegantRow key={i} label={fee.label} value={f(fee.val)} />)}
          {breakdown.tax_amount > 0 && (
            <ElegantRow
              label={`${breakdown.tax_label || "Tax"} (${breakdown.tax_rate || 0}%${breakdown.tax_system ? ` · ${breakdown.tax_system.toUpperCase()}` : ""}${breakdown.tax_inclusive ? " · Incl." : ""})`}
              value={f(breakdown.tax_amount)}
              accent="#f59e0b"
              borderTop
            />
          )}
          <ElegantRow label="Grand Total" value={f(breakdown.total_amount)} bold borderTop />
        </div>
      )}

      {!expanded && (
        <div className="payment-breakdown-summary">
          <span className="payment-breakdown-summary-text">
            Unit {f(breakdown.unit_price)}
            {breakdown.tax_amount > 0 && <span style={{ color: "#f59e0b", marginLeft: "8px" }}>+ {breakdown.tax_label || "Tax"} {f(breakdown.tax_amount)}</span>}
            {extraFees.length > 0 && <span style={{ marginLeft: "8px" }}>+ {extraFees.length} fee{extraFees.length > 1 ? "s" : ""}</span>}
          </span>
          <span className="payment-breakdown-summary-highlight">{f(breakdown.total_amount)}</span>
        </div>
      )}
    </div>
  );
};

// FullPaymentBreakdown Component
const FullPaymentBreakdown = ({ plan, unitPrice, feeBreakdown, sym = "₹" }) => {
  if (!plan?.milestones?.length) return null;
  const f = (n) => fmt(n, sym);
  const basePrice = parseFloat(String(unitPrice || "0").replace(/[^0-9.]/g, "")) || 0;
  const grandTotal = feeBreakdown?.total_amount || basePrice;

  return (
    <div className="payment-full-breakdown">
      <div className="payment-full-header">
        <div className="payment-full-title">💳 {plan.name} — Schedule</div>
        {feeBreakdown?.tax_enabled && (
          <div className="payment-full-subtitle">Grand Total: {f(grandTotal)} · Each instalment includes proportional taxes &amp; fees</div>
        )}
      </div>
      {plan.milestones.map((m, i) => {
        const isFirst = i === 0;
        const pct = parseFloat(m.percentage) || 0;
        const baseAmt = m.calculated_amount
          || (pct > 0 && basePrice > 0 ? Math.round(basePrice * pct / 100) : null)
          || m.fixed_amount || 0;
        const propTax = feeBreakdown?.tax_amount ? feeBreakdown.tax_amount * (pct / 100) : 0;
        const propFees = feeBreakdown ? (
          (feeBreakdown.processing_fee || 0) +
          (feeBreakdown.stamp_duty || 0) +
          (feeBreakdown.registration_fee || 0) +
          (feeBreakdown.maintenance_charge || 0) +
          (feeBreakdown.custom_fee1 || 0) +
          (feeBreakdown.custom_fee2 || 0)
        ) * (pct / 100) : 0;
        const propTotal = baseAmt + propTax + propFees;

        return (
          <div key={i} className={`payment-milestone-item ${i < plan.milestones.length - 1 ? 'payment-milestone-item-border' : ''} ${isFirst ? 'payment-milestone-item-first' : ''}`}>
            <div className="payment-milestone-content">
              <div className="payment-milestone-info">
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                  {isFirst && <span className="payment-milestone-badge">Due Now</span>}
                  <span className={`payment-milestone-label ${isFirst ? 'payment-milestone-label-bold' : ''}`}>{m.label}</span>
                  {pct > 0 && <span className="payment-milestone-percentage">({pct}%)</span>}
                </div>
                {m.due_date && (
                  <div className="payment-milestone-due">📅 Due: {m.due_date}</div>
                )}
                {m.due_after_days && !m.due_date && (
                  <div className="payment-milestone-due">📅 Due: {m.due_after_days} days after booking</div>
                )}
              </div>
              <div className="payment-milestone-amounts">
                <div className="payment-milestone-base">Base: {f(baseAmt)}</div>
                {propTax > 0 && <div className="payment-milestone-tax">{feeBreakdown.tax_label || "Tax"}: {f(propTax)}</div>}
                {propFees > 0 && <div className="payment-milestone-fees">Fees: {f(propFees)}</div>}
                <div className={`payment-milestone-total ${isFirst ? 'payment-milestone-total-large' : 'payment-milestone-total-muted'}`}>
                  {f(feeBreakdown?.tax_enabled ? propTotal : baseAmt)}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Manual Payment Form — used by buyers (paying manually) and by
// broker/sales agents (recording a payment collected offline).
// Always submits as PENDING; admin approval is required before it counts as paid.
const ManualPaymentForm = ({ amountDue, sym, submitting, onSubmit, onCancel, isAgent }) => {
  const [mode, setMode] = useState("cash");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");

  return (
    <div className="payment-manual-form">
      <div className="payment-section-title" style={{ marginBottom: "12px" }}>
        {isAgent ? "🏦 Record Manual Payment" : "🏦 Pay Manually"}
      </div>

      <div style={{ marginBottom: "12px" }}>
        <label className="payment-manual-label">Payment Mode</label>
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          className="payment-manual-select"
        >
          <option value="cash">💵 Cash</option>
          <option value="cheque">📄 Cheque</option>
          <option value="bank_transfer">🏦 Bank Transfer</option>
        </select>
      </div>

      <div style={{ marginBottom: "12px" }}>
        <label className="payment-manual-label">
          Reference Number <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>(cheque no. / transaction ref)</span>
        </label>
        <input
          type="text"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="Optional"
          className="payment-manual-input"
        />
      </div>

      <div style={{ marginBottom: "16px" }}>
        <label className="payment-manual-label">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any additional details…"
          rows={2}
          className="payment-manual-textarea"
        />
      </div>

      <div className="payment-manual-amount-note">
        Amount: <strong>{fmt(amountDue, sym)}</strong>
        <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "4px" }}>
          This will be submitted for admin approval and won't be marked as paid immediately.
        </div>
      </div>

      <button
        className="payment-btn payment-btn-primary"
        onClick={() => onSubmit({ payment_mode: mode, reference_number: reference, notes })}
        disabled={submitting}
      >
        {submitting ? (
          <span className="payment-btn-spinner">
            <span className="spinner"></span>
            Submitting…
          </span>
        ) : "Submit for Approval"}
      </button>

      <button className="payment-btn-secondary" onClick={onCancel} disabled={submitting}>
        ← Back
      </button>
    </div>
  );
};

// Main PaymentPage Component
export default function PaymentPage() {
  const { tenant, bookingId } = useParams();
  const navigate = useNavigate();
  // const user = useSelector(selectUser);

  const { user } = useAuth();

  const [booking, setBooking] = useState(null);
  const [gateway, setGateway] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [milestone, setMilestone] = useState(null);
  const [milestoneLoad, setMilestoneLoad] = useState(false);

  // ── New state: payment method tabs (buyer), agent actions ──────────────
  const [payMode, setPayMode] = useState("online"); // 'online' | 'manual' — buyer only
  const [showManualForm, setShowManualForm] = useState(false); // agent only
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualSubmitted, setManualSubmitted] = useState(false);
  const [linkSending, setLinkSending] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  const milestoneId = new URLSearchParams(window.location.search).get("milestone");
  const isMilestonePayment = !!milestoneId && !!milestone;
  const isAgent = user?.role === "broker" || user?.role === "sales";

  
  console.log(milestoneId);

  useEffect(() => {
    if (!user?.token) {
      navigate(`/${tenant || ''}`);
    }
  }, [user, navigate, tenant]);

  useEffect(() => {
    if (!user?.token || !bookingId || !tenant) return;

    (async () => {
      try {
        console.log("Fetching booking:", bookingId);
        const [bData, gData] = await Promise.all([
          apiJson(tenant, `/bookings/${bookingId}`, {
            headers: {
              Authorization: `Bearer ${user.token}`,
              Accept: "application/json"
            },
          }),
          apiJson(tenant, `/payment/config`, {
            headers: {
              Authorization: `Bearer ${user.token}`,
              Accept: "application/json"
            },
          }),
        ]);

        console.log("Booking data:", bData);
        console.log("Gateway data:", gData);

        setBooking(bData.booking);
        setGateway(gData);

        if (bData.booking?.booking_status === "confirmed" && !milestoneId) {
          setSuccess(true);
        }

        if (milestoneId) {
          setMilestoneLoad(true);
          try {
            const mData = await apiJson(tenant, `/bookings/${bookingId}/milestones`, {
              headers: {
                Authorization: `Bearer ${user.token}`,
                Accept: "application/json"
              },
            });
            const found = (mData.milestones || []).find((m) => String(m.id) === String(milestoneId));
            if (!found) throw new Error("Milestone not found.");
            if (found.status === "paid") setError("This milestone has already been paid.");
            setMilestone(found);
            console.log("Milestone found:", found);
          } catch (mErr) {
            console.error("Milestone error:", mErr);
            setError(mErr.message || "Failed to load milestone.");
          } finally {
            setMilestoneLoad(false);
          }
        }
      } catch (e) {
        console.error("Payment page error:", e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [bookingId, user, milestoneId, tenant]);


  const feeBreakdown = booking?.fee_breakdown || null;
  const currSym = feeBreakdown?.currency_symbol || "₹";
  const fmtAmt = (n) => fmt(n, currSym);

  const plan = booking?.payment_plan;
  const firstMilestone = plan?.milestones?.[0];
  const unitPrice = parseFloat(booking?.price || 0);

  const downPayment = isMilestonePayment
    ? (milestone?.total_with_tax ?? milestone?.amount)
    : booking?.down_payment_amount || booking?.total_amount || unitPrice;

  const isInstalment = !isMilestonePayment && plan && booking?.down_payment_amount && booking?.total_amount && booking.down_payment_amount < booking.total_amount;

  const firstMsPct = firstMilestone ? parseFloat(firstMilestone.percentage) || 0 : 100;
  const firstMsBase = feeBreakdown ? (feeBreakdown.unit_price || unitPrice) * (firstMsPct / 100) : unitPrice;
  const firstMsTax = feeBreakdown?.tax_amount ? feeBreakdown.tax_amount * (firstMsPct / 100) : 0;
  const firstMsFees = feeBreakdown ? (
    (feeBreakdown.processing_fee || 0) + (feeBreakdown.stamp_duty || 0) +
    (feeBreakdown.registration_fee || 0) + (feeBreakdown.maintenance_charge || 0) +
    (feeBreakdown.custom_fee1 || 0) + (feeBreakdown.custom_fee2 || 0)
  ) * (firstMsPct / 100) : 0;

  const payWithRazorpay = async () => {
    setPaying(true);
    setError("");

    const ok = await loadRazorpay();
    if (!ok) {
      setError("Razorpay failed to load. Please check your internet connection.");
      setPaying(false);
      return;
    }

    try {
      const bookingIdNum = parseInt(bookingId, 10);
      if (isNaN(bookingIdNum)) {
        throw new Error("Invalid booking ID");
      }

      const requestBody = {
        booking_id: bookingIdNum
      };

      if (milestoneId) {
        const milestoneIdNum = parseInt(milestoneId, 10);
        if (!isNaN(milestoneIdNum)) {
          requestBody.milestone_id = milestoneIdNum;
        }
      }

      console.log("Creating Razorpay order with:", requestBody);

      const data = await apiJson(tenant, `/payment/razorpay/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
          Accept: "application/json"
        },
        body: JSON.stringify(requestBody),
      });

      console.log("Order creation response:", data);

      if (!data.order_id) {
        throw new Error("No order ID received from server");
      }

      if (!gateway?.key) {
        throw new Error("Razorpay key not configured");
      }

      const options = {
        key: gateway.key,
        amount: data.amount,
        currency: data.currency || "INR",
        name: data.app_name || "Property Booking",
        description: isMilestonePayment
          ? `${milestone.label} · Unit ${booking?.plot_number}`
          : `Unit ${booking?.plot_number} — Booking #${bookingId}`,
        order_id: data.order_id,
        prefill: {
          name: user.name,
          email: user.email,
          contact: (user.country_code || "+91") + (user.phone || "")
        },
        theme: {
          color: getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim() || "#c9a96e"
        },
        handler: async (response) => {
          try {
            const vData = await apiJson(tenant, `/payment/razorpay/verify`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
                Accept: "application/json"
              },
              body: JSON.stringify({
                ...response,
                booking_id: bookingIdNum,
                milestone_id: milestoneId ? parseInt(milestoneId, 10) : undefined
              }),
            });
            setSuccess(true);
            setPaying(false);
          } catch (err) {
            console.error("Verification error:", err);
            setError(err.message);
            setPaying(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPaying(false);
            setError("Payment was cancelled.");
          }
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      console.error("Razorpay error:", err);
      setError(err.message);
      setPaying(false);
    }
  };

  const payWithStripe = async () => {
    setPaying(true);
    setError("");

    try {
      const stripe = await loadStripe(gateway.key);
      if (!stripe) {
        throw new Error("Stripe failed to load.");
      }

      const bookingIdNum = parseInt(bookingId, 10);
      const requestBody = { booking_id: bookingIdNum };
      if (milestoneId) {
        requestBody.milestone_id = parseInt(milestoneId, 10);
      }

      const data = await apiJson(tenant, `/payment/stripe/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
          Accept: "application/json"
        },
        body: JSON.stringify(requestBody),
      });

      const { error: stripeError } = await stripe.redirectToCheckout({ sessionId: data.session_id });
      if (stripeError) {
        throw new Error(stripeError.message);
      }
    } catch (err) {
      console.error("Stripe error:", err);
      setError(err.message);
      setPaying(false);
    }
  };

  const payWithPhonePe = async () => {
    setPaying(true);
    setError("");

    try {
      const bookingIdNum = parseInt(bookingId, 10);
      const requestBody = { booking_id: bookingIdNum };
      if (milestoneId) {
        requestBody.milestone_id = parseInt(milestoneId, 10);
      }

      const data = await apiJson(tenant, `/payment/phonepe/initiate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
          Accept: "application/json"
        },
        body: JSON.stringify(requestBody),
      });

      window.location.href = data.redirect_url;
    } catch (err) {
      console.error("PhonePe error:", err);
      setError(err.message);
      setPaying(false);
    }
  };

  const handlePay = () => {
    if (!gateway) {
      setError("No payment gateway configured.");
      return;
    }

    const p = gateway.provider?.toLowerCase();
    if (p === "razorpay") payWithRazorpay();
    else if (p === "stripe") payWithStripe();
    else if (p === "phonepe") payWithPhonePe();
    else setError(`Unknown payment provider: ${gateway.provider}`);
  };

  // ── Manual payment submission (buyer paying manually, or agent recording
  //    a payment collected offline). Always pending admin approval. ────────
  const submitManualPayment = async ({ payment_mode, reference_number, notes }) => {
    setManualSubmitting(true);
    setError("");
    try {
      const body = {
        payment_mode,
        amount: downPayment,
        reference_number: reference_number || undefined,
        notes: notes || undefined,
      };
      if (milestoneId) body.milestone_id = parseInt(milestoneId, 10);

      console.log(milestoneId);

      await apiJson(tenant, `/bookings/${bookingId}/manual-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
          Accept: "application/json"
        },
        body: JSON.stringify(body),
      });
      setManualSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setManualSubmitting(false);
    }
  };

  // ── Send payment link to client (broker/sales agents) ──────────────────
  const sendLinkToClient = async () => {
    setLinkSending(true);
    setError("");
    try {
      const body = {};
      if (milestoneId) body.milestone_id = parseInt(milestoneId, 10);

      await apiJson(tenant, `/bookings/${bookingId}/send-payment-link`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
          Accept: "application/json"
        },
        body: JSON.stringify(body),
      });
      setLinkSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLinkSending(false);
    }
  };

  const providerLabel = gateway?.provider
    ? { razorpay: "Pay with Razorpay", stripe: "Pay with Stripe", phonepe: "Pay with PhonePe" }[gateway.provider.toLowerCase()] || "Proceed to Pay"
    : "Proceed to Pay";

  // Loading state
  if (loading || milestoneLoad) return (
    <div className="payment-page">
      <div className="payment-loading">
        <div className="payment-loading-spinner">⏳</div>
        <div className="payment-loading-text">Loading payment details…</div>
      </div>
    </div>
  );

  // Already paid milestone
  if (milestoneId && milestone?.status === "paid") return (
    <div className="payment-page">
      <div className="payment-card payment-card-centered">
        <div className="payment-success-icon">✅</div>
        <div className="payment-error-title">Already Paid</div>
        <div className="payment-success-message">
          {milestone.label} for Unit <strong style={{ color: 'var(--primary-color)' }}>{booking?.plot_number}</strong> has already been paid.
        </div>
        <button className="payment-btn-outline" onClick={() => navigate(`/${tenant}/dashboard`)}>← Back to Dashboard</button>
      </div>
    </div>
  );

  // Manual payment submitted — awaiting admin approval
  if (manualSubmitted) return (
    <div className="payment-page">
      <div className="payment-card payment-card-centered">
        <div className="payment-success-icon">⏳</div>
        <div className="payment-error-title">Submitted for Approval</div>
        <div className="payment-success-message">
          {isAgent ? "The manual payment you recorded" : "Your manual payment"} for Unit{" "}
          <strong style={{ color: 'var(--primary-color)' }}>{booking?.plot_number}</strong> has been submitted
          and is awaiting confirmation from our team. {isAgent ? "You" : "You"} will be notified once it's approved.
        </div>
        <button className="payment-btn-outline" onClick={() => navigate(`/${tenant}/dashboard`)}>← Back to Dashboard</button>
      </div>
    </div>
  );

  // Success state
  if (success) return (
    <div className="payment-page">
      <div className="payment-card-success payment-card-centered">
        <div className="payment-success-icon">🎉</div>
        <div className="payment-success-title">Payment Successful!</div>
        <div className="payment-success-message">
          {isMilestonePayment
            ? <>Payment for <strong style={{ color: 'var(--primary-color)' }}>{milestone?.label}</strong> on Unit <strong style={{ color: 'var(--primary-color)' }}>{booking?.plot_number}</strong> confirmed.</>
            : <>Unit <strong style={{ color: 'var(--primary-color)' }}>{booking?.plot_number}</strong> booking confirmed. A receipt will be sent to {user?.email}.</>
          }
        </div>
        <div className="payment-details-box">
          {[
            ["Booking ID", `#${bookingId}`],
            ["Unit", booking?.plot_number],
            ...(isMilestonePayment ? [["Milestone", milestone?.label]] : []),
            ["Amount Paid", fmtAmt(downPayment)],
            ...(isMilestonePayment && milestone?.tax_amount > 0 ? [[`${milestone.tax_label || "Tax"} included`, fmtAmt(milestone.tax_amount)]] : [])
          ].map(([k, v]) => (
            <div key={k} className="payment-detail-row">
              <span className="payment-detail-label">{k}</span>
              <span className={k === "Amount Paid" ? "payment-detail-highlight" : "payment-detail-value"}>{v}</span>
            </div>
          ))}
        </div>
        <button className="payment-btn-outline" onClick={() => navigate(`/${tenant}/dashboard`)}>← Back to Dashboard</button>
      </div>
    </div>
  );

  // Error state
  if (error && !booking) return (
    <div className="payment-page">
      <div className="payment-card-error payment-card-centered">
        <div className="payment-error-icon-large">⚠️</div>
        <div className="payment-error-message">{error}</div>
        <button className="payment-btn-outline" onClick={() => navigate(-1)}>← Go Back</button>
      </div>
    </div>
  );

  // Main payment form
  return (
    <div className="payment-page">
      <div className="payment-card">
        <div className="payment-header">{isMilestonePayment ? "Milestone Payment" : "Complete Your Booking"}</div>
        <div className="payment-subheader">
          {isMilestonePayment
            ? `${milestone.label} · Booking #${bookingId} · Unit ${booking?.plot_number}`
            : `Booking #${bookingId} · ${booking?.plot_type} — Unit ${booking?.plot_number}`}
        </div>

        {/* Booking Summary */}
        <div className="payment-summary">
          <div className="payment-section-title">📋 Booking Summary</div>
          <div className="payment-summary-content">
            {[
              ["Project", booking?.project_name || booking?.project?.name || "—"],
              ["Floor", booking?.block_name || booking?.block?.name || booking?.block || "—"],
              ["Area", booking?.area ? `${booking.area} sq.ft` : "—"],
              ["Your Name", user?.name],
              ["Email", user?.email],
            ].map(([k, v]) => (
              <div key={k} className="payment-summary-row">
                <span className="payment-summary-label">{k}</span>
                <span className="payment-summary-value">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Breakdown */}
        {isMilestonePayment ? (
          <div className="payment-milestone-card">
            <div className="payment-milestone-header">💳 Milestone Payment</div>
            <ElegantRow label={`${milestone.label} (base)`} value={fmtAmt(milestone.amount)} />
            {(milestone.extra_fees || 0) > 0 && <ElegantRow label="Additional Fees" value={fmtAmt(milestone.extra_fees)} />}
            {(milestone.tax_amount || 0) > 0 && (
              <ElegantRow label={`${milestone.tax_label || "Tax"}${milestone.tax_rate > 0 ? ` (${milestone.tax_rate}%)` : ""}`} value={fmtAmt(milestone.tax_amount)} accent="#f59e0b" borderTop />
            )}
            {((milestone.tax_amount || 0) > 0 || (milestone.extra_fees || 0) > 0) && (
              <ElegantRow label="Milestone Total" value={fmtAmt(milestone.total_with_tax ?? milestone.amount)} bold borderTop />
            )}
            {milestone.due_date && <div className="payment-milestone-due-date">📅 Due: {milestone.due_date}</div>}
          </div>
        ) : plan ? (
          <>
            <GrandTotalBreakdown breakdown={feeBreakdown} sym={currSym} />
            <FullPaymentBreakdown plan={plan} unitPrice={unitPrice} feeBreakdown={feeBreakdown} sym={currSym} />
            {firstMilestone && (
              <div className="payment-due-now">
                <div className="payment-due-now-header">💚 Due Now — {firstMilestone.label}{firstMsPct > 0 ? ` (${firstMsPct}%)` : ""}</div>
                <ElegantRow label="Unit price portion" value={fmtAmt(firstMsBase)} />
                {firstMsFees > 0 && <ElegantRow label="Fees (proportional)" value={fmtAmt(firstMsFees)} />}
                {firstMsTax > 0 && (
                  <ElegantRow label={`${feeBreakdown.tax_label || "Tax"} (${feeBreakdown.tax_rate || 0}%)`} value={fmtAmt(firstMsTax)} accent="#f59e0b" borderTop />
                )}
                {(firstMsTax > 0 || firstMsFees > 0) && (
                  <ElegantRow label="First instalment total" value={fmtAmt(feeBreakdown?.tax_enabled ? firstMsBase + firstMsTax + firstMsFees : firstMsBase)} bold borderTop />
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <GrandTotalBreakdown breakdown={feeBreakdown} sym={currSym} />
            {!feeBreakdown && (
              <div style={{ margin: "16px 0 0", padding: "12px 16px", background: "var(--page-bg)", border: "1px solid var(--card-border)", borderRadius: "14px", fontSize: "13px", color: "var(--text-muted)", textAlign: "center" }}>
                💰 Full Payment — no instalment plan
              </div>
            )}
          </>
        )}

        {/* Amount Due */}
        <div className="payment-amount-section">
          <div className="payment-amount-label">
            {isMilestonePayment
              ? `${milestone.label}${milestone.tax_amount > 0 ? " (base + tax)" : ""}`
              : plan
                ? `First payment — ${firstMilestone?.label || "Down Payment"}${feeBreakdown?.tax_enabled ? " (incl. taxes & fees)" : ""}`
                : `Total amount due${feeBreakdown?.tax_enabled ? " (incl. all taxes & fees)" : ""}`
            }
          </div>
          <div className="payment-amount-value">{fmtAmt(downPayment)}</div>
          {!isMilestonePayment && feeBreakdown?.tax_amount > 0 && (
            <div className="payment-tax-badge">
              ✓ Includes {feeBreakdown.tax_label || "Tax"}: {fmtAmt(isInstalment ? firstMsTax : feeBreakdown.tax_amount)}
              {feeBreakdown.tax_rate > 0 && ` (${feeBreakdown.tax_rate}%${feeBreakdown.tax_system ? ` · ${feeBreakdown.tax_system.toUpperCase()}` : ""})`}
            </div>
          )}
          {isInstalment && (
            <div className="payment-instalment-note">
              Grand Total: {fmtAmt(booking.total_amount)} · Remaining: {fmtAmt(booking.total_amount - downPayment)} in future instalments
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="payment-error">
            <span className="payment-error-icon">⚠</span>
            {error}
          </div>
        )}

        {/* ── Broker / Sales agent view — never pay directly ─────────────── */}
        {isAgent ? (
          <>
            {!showManualForm ? (
              <>
                <div className="payment-agent-notice">
                  🤝 You're viewing this on behalf of your client. Choose an option below —
                  agents don't pay directly through the gateway.
                </div>

                {linkSent && (
                  <div className="payment-agent-success">✓ Payment link sent to the client's email.</div>
                )}

                <button
                  className="payment-btn payment-btn-primary"
                  onClick={sendLinkToClient}
                  disabled={linkSending}
                >
                  {linkSending ? (
                    <span className="payment-btn-spinner">
                      <span className="spinner"></span>
                      Sending…
                    </span>
                  ) : "📧 Send Payment Link to Client"}
                </button>

                <button
                  className="payment-btn-outline"
                  onClick={() => setShowManualForm(true)}
                  style={{ marginTop: "10px" }}
                >
                  🏦 Record Manual Payment
                </button>
              </>
            ) : (
              <ManualPaymentForm
                amountDue={downPayment}
                sym={currSym}
                submitting={manualSubmitting}
                onSubmit={submitManualPayment}
                onCancel={() => setShowManualForm(false)}
                isAgent
              />
            )}
          </>
        ) : (
          /* ── Buyer view — Online / Manual tabs, both always available ── */
          <>
            <div className="payment-mode-tabs">
              <button
                className={`payment-mode-tab ${payMode === "online" ? "payment-mode-tab-active" : ""}`}
                onClick={() => setPayMode("online")}
              >
                💳 Pay Online
              </button>
              <button
                className={`payment-mode-tab ${payMode === "manual" ? "payment-mode-tab-active" : ""}`}
                onClick={() => setPayMode("manual")}
              >
                🏦 Pay Manually
              </button>
            </div>

            {payMode === "online" ? (
              <>
                {!gateway && (
                  <div className="payment-gateway-warning">⚠ No payment gateway configured. Please contact admin.</div>
                )}
                <button
                  className="payment-btn payment-btn-primary"
                  onClick={handlePay}
                  disabled={paying || !gateway || milestone?.status === "paid"}
                >
                  {paying ? (
                    <span className="payment-btn-spinner">
                      <span className="spinner"></span>
                      Processing...
                    </span>
                  ) : providerLabel}
                </button>
              </>
            ) : (
              <ManualPaymentForm
                amountDue={downPayment}
                sym={currSym}
                submitting={manualSubmitting}
                onSubmit={submitManualPayment}
                onCancel={() => setPayMode("online")}
              />
            )}
          </>
        )}

        {/* Cancel Button */}
        <button className="payment-btn-secondary" onClick={() => navigate(-1)}>
          ← Cancel &amp; Go Back
        </button>
      </div>
    </div>
  );
}
